import FeatureFlag from '../form/feature-flag/FeatureFlag';
import { createContext } from 'react';
import { ContactFormConnector } from '../../services/connectors/ContactFormConnector';
import { TrackEventRequest } from '../../services/connectors/TrackEventRequest';
import { FeatureFlagRequest } from '../../services/connectors/FeatureFlagRequest';

export const PHONE_NUMBER_HIDDEN_FEATURE_FLAG = 'contact-form_phone-hidden';
export const TAKE_A_PICTURE_FEATURE_FLAG = 'contact-form_show-take-picture';
export const SELF_RETURN_REMINDER_DISPLAYED_FEATURE_FLAG =
  'contact-form_self-return-reminder-displayed';
export const LIVECHAT_ICON_FEATURE_FLAG = 'contact-form_livechat-icon';

export const SUPPORTED_FEATURE_FLAGS = [
  PHONE_NUMBER_HIDDEN_FEATURE_FLAG,
  TAKE_A_PICTURE_FEATURE_FLAG,
  SELF_RETURN_REMINDER_DISPLAYED_FEATURE_FLAG,
  LIVECHAT_ICON_FEATURE_FLAG
];

const EMPTY_VARIATION = '';
const ON_VARIATION_NAME = 'on';

export const ORDER_STATUS_DISPLAYED_EXPERIMENT = 'contact-form_order-status-displayed'; // this is both experiment and feature flag for other audiences

const clickTrackEventType = 'clickTrack';
const userTrackingEventType = 'user-tracking';

const optimizelyExperimentationCode = 'ope';

export type UserTrackingEvent = {
  adobe: string;
  optimizely: string;
};

export const UserTrackingEvents = Object.freeze({
  FAQ_CONTACT_US_FORM_SUBMIT: {
    adobe: 'FAQ - Contact us form submit',
    optimizely: 'contact-form_form-submitted',
  },
  CF_PHONE_NUMBER_PARSING_ERROR: {
    adobe: 'Error while parsing the phone number',
    optimizely: '',
  },
  CF_ORDER_AND_PARCEL_DELIVERY_STATUS_DISPLAYED: {
    adobe: 'Contact Form - Order & Parcel delivery status displayed',
    optimizely: '',
  },
  CF_SUBMITTED_ORDER_AND_PARCEL_DELIVERY_STATUS_DISPLAYED: {
    adobe: 'Contact Form Submitted - Order & Parcel delivery status displayed',
    optimizely: '',
  },
  CF_ORDER_AND_PARCEL_DELIVERY_STATUS_DISPLAYED_NEED_MORE_HELP: {
    adobe: 'Contact Form - Order & Parcel delivery status displayed - I need more help',
    optimizely: 'contact-form_i-need-more-help-clicked',
  },
  CF_CALL_US: {
    adobe: 'Contact Form - Call us',
    optimizely: 'contact-form_call-us-clicked',
  },
  CF_SELF_RETURN_REMINDER_DISPLAYED: {
    adobe: 'Contact Form - Self-Return reminder displayed',
    optimizely: '',
  },
  CF_SELF_RETURN_REMINDER_ORDER_DETAILS_LINK_CLICKED: {
    adobe: 'Contact Form - Self-Return reminder - Order Details link clicked',
    optimizely: '',
  },
  CF_SELF_RETURN_REMINDER_NEED_MORE_HELP_CLICKED: {
    adobe: 'Contact Form - Self-Return reminder - Need More Help button clicked',
    optimizely: '',
  },
  CF_SELF_CANCELLATION_REMINDER_DISPLAYED: {
    adobe: 'Contact Form - Self-Cancellation reminder displayed',
    optimizely: '',
  },
  CF_SELF_CANCELLATION_REMINDER_ORDER_IS_CANCELLABLE_DISPLAYED: {
    adobe: 'Contact Form - Self-Cancellation reminder - Order is cancellable displayed',
    optimizely: '',
  },
  CF_SELF_CANCELLATION_REMINDER_ORDER_IS_NOT_CANCELLABLE_DISPLAYED: {
    adobe: 'Contact Form - Self-Cancellation reminder - Order is not cancellable displayed',
    optimizely: '',
  },
  CF_SELF_CANCELLATION_REMINDER_ORDER_DETAILS_LINK_CLICKED: {
    adobe: 'Contact Form - Self-Cancellation reminder - Order Details link clicked',
    optimizely: '',
  },
  CF_SELF_CANCELLATION_REMINDER_NEED_MORE_HELP_CLICKED: {
    adobe: 'Contact Form - Self-Cancellation reminder - Need More Help button clicked',
    optimizely: '',
  },
});

export class Experimentation {
  private readonly connector: ContactFormConnector;
  private readonly experiments: Map<string, FeatureFlag>;

  constructor(
    private readonly featureFlags: FeatureFlag[] = [],
    private readonly domain: string = '',
  ) {
    this.connector = new ContactFormConnector();
    this.experiments = new Map();
  }

  get flags() {
    return this.featureFlags;
  }

  trackClick(event: UserTrackingEvent, tags: Record<string, string> = {}): void {
    this.notifyAdobeClickTrack(event);
    this.notifyOptimizely(event, this.domain, tags);
  }

  trackMetric(event: UserTrackingEvent, tags: Record<string, string> = {}): void {
    this.notifyAdobeUserTracking(event);
    this.notifyOptimizely(event, this.domain, tags);
  }

  async isExperimentOn(experimentName: string): Promise<boolean> {
    return (await this.getExperimentVariationWithFallback(experimentName)) === ON_VARIATION_NAME;
  }

  private async getExperimentVariationWithFallback(experimentName: string): Promise<string> {
    try {
      const variation = await this.getExperiment(experimentName);
      if (!variation) {
        return EMPTY_VARIATION;
      }
      return variation.variation ?? EMPTY_VARIATION;
    } catch {
      return EMPTY_VARIATION;
    }
  }

  private async getExperiment(experimentName: string): Promise<FeatureFlag | undefined> {
    const localExperiment = this.experiments.get(experimentName);
    if (localExperiment) {
      return localExperiment;
    }
    return this.getExperimentAndCache(experimentName);
  }

  private async getExperimentAndCache(experimentName: string) {
    const flagsRequest = new FeatureFlagRequest(this.domain, [experimentName]);
    const experimentFeatureFlag = await this.connector.requestFeatureFlags(flagsRequest);
    const remoteExperiment = experimentFeatureFlag.find((it) => it.key === experimentName);
    if (remoteExperiment) {
      const flag = new FeatureFlag(
        remoteExperiment.key,
        remoteExperiment.variation,
        remoteExperiment.enabled,
      );
      this.notifyExperimentVariation(flag);
      this.experiments.set(experimentName, flag);
      return flag;
    }
    return undefined;
  }

  private notifyAdobeClickTrack(event: UserTrackingEvent) {
    if (event.adobe) {
      this.notifyAdobe([clickTrackEventType, event.adobe]);
    }
  }

  private notifyAdobeUserTracking(event: UserTrackingEvent) {
    if (event.adobe) {
      this.notifyAdobe([event.adobe]);
    }
  }

  private notifyAdobe(argsArray: string[]) {
    const messagePayload: PostMessageEventPayload = {
      type: userTrackingEventType,
      payload: {
        functionName: 'SendAdobeData',
        argsArray,
      },
    };
    window.parent && window.parent.postMessage(messagePayload, '*');
  }

  private notifyOptimizely(event: UserTrackingEvent, domain: string, tags: Record<string, string>) {
    if (event.optimizely) {
      this.connector.trackEvent(new TrackEventRequest(event.optimizely, domain, tags));
    }
  }

  private notifyExperimentVariation(featureFlag: FeatureFlag) {
    const messagePayload: PostMessageEventPayload = {
      type: userTrackingEventType,
      payload: {
        functionName: 'SendABTestData',
        argsArray: [optimizelyExperimentationCode, featureFlag.key, featureFlag.variation ?? ''],
      },
    };
    window.parent && window.parent.postMessage(messagePayload, '*');
  }

  isLivechatIconDisplayed = () => !!this.findFlag(LIVECHAT_ICON_FEATURE_FLAG)?.enabled;

  isPhoneNumberHidden = () => !!this.findFlag(PHONE_NUMBER_HIDDEN_FEATURE_FLAG)?.enabled;

  isSelfReturnReminderDisplayed = () =>
    !!this.findFlag(SELF_RETURN_REMINDER_DISPLAYED_FEATURE_FLAG)?.enabled;

  isTakeAPictureDisplayed = () => !!this.findFlag(TAKE_A_PICTURE_FEATURE_FLAG)?.enabled;

  private findFlag(flagKey: string): FeatureFlag | undefined {
    return this.featureFlags?.find((it) => it.key === flagKey);
  }

  async isOrderStatusDetailsExperimentEnabled() {
    return this.isExperimentOn(ORDER_STATUS_DISPLAYED_EXPERIMENT);
  }
}

export const ExperimentationContext = createContext(new Experimentation());
